import {
    getRuleEntityIds,
    resolveRuleTargetEntityId,
    SplitAction,
} from "@common/helpers/rules";
import { SplitConfigSchema } from "@common/schemas/rule.schema";
import { Account } from "@common/types/domains/accounting/account";
import { FiltersTypes } from "@common/types/filters/general";
import {
    RuleActionType,
    RuleConditionType,
    TransactionRule,
    TransactionRuleAction,
    TransactionRuleCondition,
    parseAccountIdActionValue,
    parseAccrualAccountActionValue,
} from "@common/types/transactionRule";
import { findAccountByStoredName } from "@components/accounts/findAccountByStoredName";

export type RuleValueFilters = Pick<
    FiltersTypes,
    "counterpartiesIds" | "classes" | "coaAccounts" | "accrualCoaAccounts"
> & {
    ledgerAccounts?: string[];
};

type WorkspaceAccount = Account & { entityId: number };

type SplitLine = SplitAction["splits"][number];

type RuleShape = Pick<TransactionRule, "conditions" | "actions">;

interface RuleMatchesValueFiltersParams {
    rule: RuleShape;
    filters: RuleValueFilters;
    accounts: WorkspaceAccount[];
}

export function ruleMatchesValueFilters({
    rule,
    filters,
    accounts,
}: RuleMatchesValueFiltersParams): boolean {
    const splitLines = readSplitLines(rule.actions);
    const matchesCashAccounts = (selectedIds: string[] | undefined) =>
        matchesAccountSelection({
            selectedIds,
            rule,
            accounts,
            referencedIds: collectCashAccountIds(rule.actions, splitLines),
            storedNames: collectStoredNames(
                rule.actions,
                RuleActionType.ACCOUNT_NAME,
            ),
        });
    const matchesAccrualAccounts = (selectedIds: string[] | undefined) =>
        matchesAccountSelection({
            selectedIds,
            rule,
            accounts,
            referencedIds: collectAccrualAccountIds(rule.actions, splitLines),
            storedNames: collectStoredNames(
                rule.actions,
                RuleActionType.ACCRUAL_ACCOUNT_NAME,
            ),
        });

    return (
        matchesSelection(
            filters.counterpartiesIds,
            collectCounterpartyIds(rule, splitLines),
        ) &&
        matchesSelection(filters.classes, collectClassIds(rule, splitLines)) &&
        matchesCashAccounts(filters.coaAccounts) &&
        matchesAccrualAccounts(filters.accrualCoaAccounts) &&
        (!filters.ledgerAccounts?.length ||
            matchesCashAccounts(filters.ledgerAccounts) ||
            matchesAccrualAccounts(filters.ledgerAccounts))
    );
}

function matchesSelection(
    selectedIds: string[] | undefined,
    referencedIds: string[],
): boolean {
    if (!selectedIds?.length) {
        return true;
    }
    const selected = new Set(selectedIds);
    return referencedIds.some((id) => selected.has(id));
}

function matchesAccountSelection({
    selectedIds,
    rule,
    accounts,
    referencedIds,
    storedNames,
}: {
    selectedIds: string[] | undefined;
    rule: RuleShape;
    accounts: WorkspaceAccount[];
    referencedIds: string[];
    storedNames: string[];
}): boolean {
    if (!selectedIds?.length) {
        return true;
    }
    if (matchesSelection(selectedIds, referencedIds)) {
        return true;
    }
    if (storedNames.length === 0) {
        return false;
    }
    const selected = new Set(selectedIds);
    const targetEntityIds = readTargetEntityIds(rule);
    const candidates = accounts.filter((account) => {
        const isSelected = selected.has(account.id);
        const isTargetEntity =
            targetEntityIds === null ||
            targetEntityIds.includes(account.entityId);
        return isSelected && isTargetEntity;
    });
    return storedNames.some(
        (storedName) =>
            findAccountByStoredName(storedName, candidates) !== undefined,
    );
}

function readTargetEntityIds(rule: RuleShape): number[] | null {
    const resolveFor = (transactionEntityId: number) =>
        resolveRuleTargetEntityId({
            actions: rule.actions,
            transactionEntityId,
        });
    const ruleEntityIds = getRuleEntityIds(rule);
    if (ruleEntityIds === null) {
        const targetEntityId = resolveFor(NaN);
        return Number.isNaN(targetEntityId) ? null : [targetEntityId];
    }
    return [...new Set(ruleEntityIds.map(resolveFor))];
}

function readSplitLines(actions: TransactionRuleAction[]): SplitLine[] {
    return actions.flatMap((action) => {
        if (
            action.actionType !== RuleActionType.SPLIT ||
            typeof action.value !== "string"
        ) {
            return [];
        }
        try {
            const parsed = SplitConfigSchema.innerType().safeParse(
                JSON.parse(action.value),
            );
            return parsed.success ? parsed.data.splits : [];
        } catch {
            return [];
        }
    });
}

function readStringIds(value: TransactionRuleCondition["value"]): string[] {
    if (typeof value === "string") {
        return [value];
    }
    if (Array.isArray(value)) {
        return value.filter((item): item is string => typeof item === "string");
    }
    return [];
}

function collectCounterpartyIds(
    rule: RuleShape,
    splitLines: SplitLine[],
): string[] {
    return [
        ...rule.conditions.flatMap((condition) =>
            condition.conditionType === RuleConditionType.COUNTERPARTY
                ? readStringIds(condition.value)
                : [],
        ),
        ...rule.actions.flatMap((action) =>
            (action.actionType === RuleActionType.COUNTERPARTY ||
                action.actionType === RuleActionType.CUSTOMER) &&
            typeof action.value === "string"
                ? [action.value]
                : [],
        ),
        ...splitLines.flatMap((line) =>
            line.counterpartyId ? [line.counterpartyId] : [],
        ),
    ];
}

function collectClassIds(rule: RuleShape, splitLines: SplitLine[]): string[] {
    return [
        ...rule.conditions.flatMap((condition) =>
            condition.conditionType === RuleConditionType.CLASS
                ? readStringIds(condition.value)
                : [],
        ),
        ...rule.actions.flatMap((action) =>
            action.actionType === RuleActionType.CLASS
                ? readStringIds(action.value)
                : [],
        ),
        ...splitLines.flatMap((line) => line.classIds ?? []),
    ];
}

function collectCashAccountIds(
    actions: TransactionRuleAction[],
    splitLines: SplitLine[],
): string[] {
    return [
        ...actions.flatMap((action) =>
            action.actionType === RuleActionType.ACCOUNT_ID
                ? (parseAccountIdActionValue(action.value) ?? [])
                : [],
        ),
        ...splitLines.flatMap((line) =>
            line.accountId ? [line.accountId] : [],
        ),
    ];
}

function collectAccrualAccountIds(
    actions: TransactionRuleAction[],
    splitLines: SplitLine[],
): string[] {
    return [
        ...actions.flatMap((action) =>
            action.actionType === RuleActionType.ACCRUAL_ACCOUNT_ID
                ? (parseAccrualAccountActionValue(action.value).accountIds ??
                  [])
                : [],
        ),
        ...splitLines.flatMap((line) =>
            line.accrualAccountId ? [line.accrualAccountId] : [],
        ),
    ];
}

function collectStoredNames(
    actions: TransactionRuleAction[],
    actionType:
        | RuleActionType.ACCOUNT_NAME
        | RuleActionType.ACCRUAL_ACCOUNT_NAME,
): string[] {
    return actions.flatMap((action) => {
        if (action.actionType !== actionType) {
            return [];
        }
        if (actionType === RuleActionType.ACCRUAL_ACCOUNT_NAME) {
            const { accountName } = parseAccrualAccountActionValue(
                action.value,
            );
            return accountName ? [accountName] : [];
        }
        return typeof action.value === "string" ? [action.value] : [];
    });
}
